import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useCallback, useEffect, useRef, useState } from 'react';
import { evaluateSource } from '../../../../../behaviors/src/lib/evaluateSource';
import store from '../../../../reducers/storeRegistration';
import logger from '../../../../utils/logger';
import { getProjectRowFromMap } from './tvcIssuesSectionHelpers';

/**
 * When a project is selected, clears the issues content path, runs `tvcIssuesBehavior` via
 * `evaluateSource`, and writes the result back. Refresh (same project/active/path) skips the
 * clear so the UI keeps showing prior data until the new result is written.
 */
const useTvcIssuesProjectLoad = _ref => {
  let selectedProjectUuid = _ref.selectedProjectUuid,
    tvcIssuesBehavior = _ref.tvcIssuesBehavior,
    active = _ref.active,
    change = _ref.change,
    changeQuietly = _ref.changeQuietly,
    path = _ref.path,
    parentContentPath = _ref.parentContentPath,
    reduxEntity = _ref.reduxEntity,
    nativeFunctions = _ref.nativeFunctions,
    projectsContent = _ref.projectsContent;
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    loadingIssues = _useState2[0],
    setLoadingIssues = _useState2[1];
  const _useState3 = useState(0),
    _useState4 = _slicedToArray(_useState3, 2),
    refreshNonce = _useState4[0],
    setRefreshNonce = _useState4[1];
  const projectsContentRef = useRef(projectsContent);
  projectsContentRef.current = projectsContent;
  /** Stable key for “same load context” — refresh bumps nonce only, so we skip clearing Redux. */
  const lastCompletedLoadKeyRef = useRef(null);
  const refreshIssues = useCallback(() => {
    setRefreshNonce(n => n + 1);
  }, []);
  useEffect(() => {
    if (!selectedProjectUuid || !tvcIssuesBehavior || typeof tvcIssuesBehavior !== 'object' || !(active !== null && active !== void 0 && active.id) || !change) {
      return undefined;
    }
    const projectRow = getProjectRowFromMap(projectsContentRef.current, selectedProjectUuid);
    if (projectRow == null) {
      return undefined;
    }
    let cancelled = false;
    const run = async () => {
      setLoadingIssues(true);
      const loadKey = "".concat(selectedProjectUuid, "|").concat(active.id, "|").concat(path);
      const skipClear = lastCompletedLoadKeyRef.current === loadKey && refreshNonce > 0;
      try {
        const writePath = "content".concat(path);
        if (!skipClear) {
          if (changeQuietly) {
            changeQuietly(active.id, writePath, {});
          } else {
            await change(active.id, writePath, {});
          }
        }
        const _store$getState$redux = store.getState()[reduxEntity].active,
          _store$getState$redux2 = _store$getState$redux === void 0 ? {} : _store$getState$redux,
          contentForEval = _store$getState$redux2.content;
        const mapKey = selectedProjectUuid;
        const project = {
          title: projectRow.title,
          uuid: projectRow.uuid != null && String(projectRow.uuid).trim() !== '' ? projectRow.uuid : mapKey
        };
        const sourceWithProject = _objectSpread(_objectSpread({}, tvcIssuesBehavior), {}, {
          project
        });
        const result = await evaluateSource(sourceWithProject, contentForEval || {}, parentContentPath, {
          project
        }, nativeFunctions);
        if (!cancelled) {
          if (changeQuietly) {
            changeQuietly(active.id, writePath, result !== null && result !== void 0 ? result : {});
          } else {
            await change(active.id, writePath, result !== null && result !== void 0 ? result : {});
          }
          lastCompletedLoadKeyRef.current = loadKey;
        }
      } catch (err) {
        logger('tvcIssuesSection loadIssues:', err === null || err === void 0 ? void 0 : err.message, err === null || err === void 0 ? void 0 : err.statusCode);
        /* errorHandler already notified */
      } finally {
        if (!cancelled) {
          setLoadingIssues(false);
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [active === null || active === void 0 ? void 0 : active.id, change, changeQuietly, nativeFunctions, parentContentPath, path, reduxEntity, selectedProjectUuid, tvcIssuesBehavior, refreshNonce]);
  return {
    loadingIssues,
    refreshIssues
  };
};
export default useTvcIssuesProjectLoad;